import { Component, Input, OnInit } from '@angular/core';
import { EMPTY, Subscription, forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { expand, finalize, pairwise } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import _ from 'lodash';
import moment from 'moment';

import { ChartDataset, ChartLabel } from 'src/app/core/entities/chart-dataset';
import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartDescription } from 'src/app/core/entities/chart-description';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';
import { Patient } from 'src/app/core/entities/patient.entity';

@Component({
  selector: 'app-dexterity',
  templateUrl: './dexterity.component.html',
  styleUrls: ['./dexterity.component.css'],
})
@AutoUnsub()
export class DexterityComponent implements OnInit {
  @Input() public patientProfile: Patient = new Patient();
  private _observers: Subscription = new Subscription();
  private _features: Feature[] = [];
  public evaluation: ChartEvaluation = {
    evaluationCode: EvaluationService.EVALUATION_DEXTERITY,
    features: [
      {
        featureId: FeatureService.FEATURE_DRAW_LEFT_PWD_MEAN,
        label: [
          $localize`:@@patient_evaluationDexterity_featureDrawLeftPWDMeanLabelPart1:Precision`,
          $localize`:@@patient_evaluationDexterity_featureDrawLeftPWDMeanLabelPart2:(deviation from the plot in mm)`,
        ],
        min: -6,
        max: 0,
        isMinLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#0e5cdd',
          tooltip: {
            label: $localize`:@@patient_evaluationDexterity_featureDrawLeftPWDMeanTooltipLabel:Offset with the plot`,
            unit: 'mm',
          },
          pointStyle: 'circle',
        },
      },
      {
        featureId: FeatureService.FEATURE_DRAW_LEFT_CT_MEAN,
        label: $localize`:@@patient_evaluationDexterity_featureDrawLeftCTMeanLabel:Average duration of plot (in s)`,
        min: 0,
        max: 4,
        isMaxLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#0dc49c',
          tooltip: {
            label: $localize`:@@patient_evaluationDexterity_featureDrawLeftCTMeanTooltipLabel:Duration`,
            unit: 's',
          },
          hidden: true,
          pointStyle: 'circle',
          nbSteps: 4,
          axis: {
            id: 'drawLeftCtMean',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
      {
        featureId: FeatureService.FEATURE_DRAW_LEFT_DS_MEAN,
        label: $localize`:@@patient_evaluationDexterity_featureDrawLeftDSMeanLabel:Speed of plot (in cm/s)`,
        min: 0,
        max: 15,
        isMaxLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#6225e1',
          tooltip: {
            label: $localize`:@@patient_evaluationDexterity_featureDrawLeftDSMeanTooltipLabel:Speed of plot`,
            unit: 'cm/s',
          },
          hidden: true,
          pointStyle: 'circle',
          nbDecimalDigits: 1,
          axis: {
            id: 'drawLeftAverageSpeedAccuracy',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
      {
        featureId: FeatureService.FEATURE_DRAW_RIGHT_PWD_MEAN,
        label: [
          $localize`:@@patient_evaluationDexterity_featureDrawRightPWDMeanLabelPart1:Precision`,
          $localize`:@@patient_evaluationDexterity_featureDrawRightPWDMeanpLabelPart2:(deviation from the plot in mm)`,
        ],
        min: -6,
        max: 0,
        isMinLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#8893aa',
          tooltip: {
            label: $localize`:@@patient_evaluationDexterity_featureDrawRightPWDMeanTooltipLabel:Offset with the plot`,
            unit: 'mm',
          },
          pointStyle: 'circle',
        },
      },
      {
        featureId: FeatureService.FEATURE_DRAW_RIGHT_CT_MEAN,
        label: $localize`:@@patient_evaluationDexterity_featureDrawRightCTMeanLabel:Average duration of plot (in s)`,
        min: 0,
        max: 4,
        isMaxLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#0dc49c',
          tooltip: {
            label: $localize`:@@patient_evaluationDexterity_featureDrawRightCTMeanTooltipLabel:Duration`,
            unit: 's',
          },
          hidden: true,
          nbSteps: 4,
          pointStyle: 'circle',
          axis: {
            id: 'drawRightCtMean',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
      {
        featureId: FeatureService.FEATURE_DRAW_RIGHT_DS_MEAN,
        label: $localize`:@@patient_evaluationDexterity_featureDrawRightDSMeanLabel:Speed of plot (in cm/s)`,
        min: 0,
        max: 15,
        isMaxLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#6225e1',
          tooltip: {
            label: $localize`:@@patient_evaluationDexterity_featureDrawRightDSMeanTooltipLabel:Speed of plot`,
            unit: 'cm/s',
          },
          hidden: true,
          nbDecimalDigits: 1,
          pointStyle: 'circle',
          axis: {
            id: 'drawRightDsMean',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
    ],
    horizontalLimitLines: [
      {
        label: $localize`:@@patient_evaluationDexterity_horizontalLimitLineYLabel:Maximum precision`,
        color: '#3d3d40',
        assignToAxisIds: {
          y: 0,
        },
      },
    ],
  };
  public leftHandDatasets: ChartDataset[] = [];
  public rightHandDatasets: ChartDataset[] = [];
  public xLabels: ChartLabel[] = [];
  public chartDescription: ChartDescription;
  public dateRangeForm: FormGroup;
  public loading = true;
  public loadingChart = true;

  constructor(
    private featureService: FeatureService,
    private chartService: ChartService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.prepareDateRangeForm();

    this.resetChartDescription();
  }

  prepareDateRangeForm(): void {
    const startDate = moment.utc().subtract(6, 'months').format('MM/YYYY');
    const endDate = moment.utc().format('MM/YYYY');

    this.dateRangeForm = this.formBuilder.group({
      startDate: [
        startDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
      endDate: [
        endDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
    });

    this._observers.add(
      this.dateRangeForm.valueChanges.pipe(pairwise()).subscribe({
        next: ([previousRes, res]) => this.submitDateRangeForm(),
      })
    );

    this.loading = false;
  }

  changeDateRange(
    event: { monthIndex: number; year: number },
    key: string
  ): void {
    const value = moment
      .utc([event.year, event.monthIndex, 1])
      .format('MM/YYYY');

    this.dateRangeForm.get(key).setValue(value);
  }

  getDateYear(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return splitDate[1];
  }

  getDateMonth(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return parseInt(splitDate[0], 10) - 1;
  }

  submitDateRangeForm(): void {
    if (this.dateRangeForm.valid) {
      this.loadingChart = true;
      this.setChart();
    }
  }

  resetChartDescription(): void {
    this.chartDescription = {
      evaluationCode: this.evaluation.evaluationCode,
      evaluationLabel: $localize`:@@patient_evaluationDexterity_chartDescriptionLabel:of light dexterity`,
      measuringPointsTotal: 0,
      featuresGroups: [
        {
          label: $localize`:@@patient_evaluationDexterity_chartDescriptionFeatureGroupLeftHandLabel:To left hand :`,
          features: [],
        },
        {
          label: $localize`:@@patient_evaluationDexterity_chartDescriptionFeatureGroupRightHandLabel:To right hand :`,
          features: [],
        },
      ],
    };
  }

  setChart(): void {
    this._features = [];
    this.leftHandDatasets = [];
    this.rightHandDatasets = [];
    this.xLabels = [];

    // Reset chart description
    this.resetChartDescription();

    const startDate = moment.utc(
      this.dateRangeForm.get('startDate').value,
      'MM/YYYY'
    );
    const endDate = moment.utc(
      this.dateRangeForm.get('endDate').value,
      'MM/YYYY'
    );

    let params = {
      page: 1,
      startDate: startDate.toISOString(),
      endDate: endDate.endOf('month').toISOString(),
      patientUuid: this.route.snapshot.params['uuid'],
      featureIds: this.evaluation.features.map((item) => item.featureId),
    };

    const source$ = this.featureService.getFeatures(params).pipe(
      expand((res) => {
        params.page++;
        this._features = this._features.concat(res);
        return res.length < 50
          ? EMPTY
          : this.featureService.getFeatures(params);
      })
    );

    forkJoin([source$])
      .pipe(
        finalize(() => {
          this.loadingChart = false;
        })
      )
      .subscribe((res) => {
        let chartDataPoints = {};

        this.evaluation.features.forEach((feature) => {
          let groupedFeatures = this.featureService.groupFeatures(
            this._features,
            feature.featureId
          );
          chartDataPoints[feature.featureId] =
            this.chartService.setChartDataPoints(groupedFeatures);
        });

        if (Object.values(chartDataPoints).length > 0) {
          // Set labels
          const maxDataPoints = Object.values(chartDataPoints).reduce(
            (r: any, s: any) => (r.concat(s)),
            []
          );
          this.xLabels = this.chartService.setXLabels(
            maxDataPoints as ChartDataPoint[]
          );

          // Set datasets
          this.evaluation.features.forEach((feature) => {
            const dataset = this.chartService.setDataset(
              feature,
              chartDataPoints[feature.featureId],
              feature.chartDatasetOption,
              [],
              this.xLabels,
              feature.min ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].min
                  : null),
              feature.max ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].max
                  : null),
              feature.isMaxLimited,
              feature.isMinLimited
            );
            if (this.isLeftHandFeature(feature.featureId)) {
              this.leftHandDatasets.push(dataset);
              this.chartDescription.featuresGroups[0].features.push({
                featureId: feature.featureId,
                hidden: feature.chartDatasetOption.hidden,
                data: dataset.data,
                unit: feature.chartDatasetOption.tooltip
                  ? feature.chartDatasetOption.tooltip.unit
                  : '',
                label: Array.isArray(feature.label)
                  ? feature.label[0]
                  : feature.label,
                labelSuffix:
                  feature.featureId == FeatureService.FEATURE_DRAW_LEFT_PWD_MEAN
                    ? Array.isArray(feature.label)
                      ? feature.label[0]
                      : feature.label
                    : '',
              });
            } else {
              this.chartDescription.featuresGroups[1].features.push({
                featureId: feature.featureId,
                hidden: feature.chartDatasetOption.hidden,
                data: dataset.data,
                unit: feature.chartDatasetOption.tooltip
                  ? feature.chartDatasetOption.tooltip.unit
                  : '',
                label: Array.isArray(feature.label)
                  ? feature.label[0]
                  : feature.label,
                labelSuffix:
                  feature.featureId ==
                  FeatureService.FEATURE_DRAW_RIGHT_PWD_MEAN
                    ? Array.isArray(feature.label)
                      ? feature.label[0]
                      : feature.label
                    : '',
              });
              this.rightHandDatasets.push(dataset);
            }
          });
          _.cloneDeep(this.chartDescription.featuresGroups)
            .reduce((origin, value) => {
              origin.features = origin.features.concat(value.features);
              return origin;
            })
            .features.forEach((feature, i) => {
              if (
                feature.data.filter((item) => item.value !== null).length >
                this.chartDescription.measuringPointsTotal
              ) {
                this.chartDescription.measuringPointsTotal =
                  feature.data.filter((item) => item.value !== null).length;
              }
            });
        }
      });
  }

  private isLeftHandFeature(featureId: string): boolean {
    const leftHandFeatures = [
      FeatureService.FEATURE_DRAW_LEFT_DS_MEAN,
      FeatureService.FEATURE_DRAW_LEFT_CT_MEAN,
      FeatureService.FEATURE_DRAW_LEFT_PWD_MEAN,
    ];
    return leftHandFeatures.includes(featureId);
  }

  updateChartDescriptionFeature(chartDataset: ChartDataset): void {
    this.chartDescription.featuresGroups.forEach((featureGroup) => {
      let feature = featureGroup.features.find(
        (item) => item.featureId == chartDataset.id
      );
      if (feature) {
        feature.hidden = chartDataset.hidden;
      }
    });
  }
}
